import { useEffect, useSyncExternalStore } from 'react';

/**
 * ─── ⭐ Die Anwendung aktualisiert sich selbst ──────────────────────────────
 *
 * DIE REGEL: Aktualisiert wird, was sich OHNE ZUTUN des Nutzers ändert. Nicht „wichtig gegen
 * unwichtig" — sondern: Ändert eine fremde Quelle die Daten, oder nur der Nutzer selbst?
 *
 *   ✅ Kurse, Depotwert, Positionen · Watchlist · Marktlage · Glocke · Marktstatus
 *   ⛔ Profil, Einstellungen · Ziele, Sparpläne, Konten · Academy, Steuer, Rechnungen · Admin
 *
 * ⚠️ ZIELE SIND DER GRENZFALL: Ihr Fortschritt hängt am Depotwert, ändert sich also von selbst.
 * Aber die Seite ist ein BEARBEITUNGSORT — dort wird nicht im Takt aktualisiert, sondern beim
 * Öffnen frisch geladen. Das reicht, und es nimmt niemandem die Eingabe unter den Fingern weg.
 *
 * ⛔ WAS ES NICHT GIBT: keine WebSockets, kein Server-Push. Bei Tagesschlusskursen und einem
 * Vier-Stunden-Ingest wäre das Infrastruktur ohne Gegenwert. Und Echtzeitkurse gibt es im
 * Builder-Tarif ohnehin nicht — eine Seite, die im Minutentakt fragt, bekäme den ganzen Tag
 * dieselbe Zahl. Der Gewinn liegt darin, dass nach dem Kurs-Cron oder einem Klick auf „Kurse
 * aktualisieren" die neuen Werte OHNE F5 erscheinen.
 */

/**
 * ⭐ EIN ORT für die Takte. Verstreute Zahlen driften auseinander, und niemand findet später,
 * warum eine Seite häufiger fragt als die andere.
 *
 * ⛔ Der Takt richtet sich nach der QUELLE, nicht nach der Wichtigkeit der Seite. Häufiger zu
 * fragen, als sich etwas ändert, kostet nur Anfragen — und bei Eulerpool ein Kontingent.
 */
export const AKTUALISIERUNG = {
  /** Kurse und alles, was daran hängt. Die Quelle liefert einmal täglich. */
  kurse: 15 * 60 * 1000,
  /** Marktlage-Ereignisse. Der Ingest läuft alle vier Stunden — aber auch von Hand. */
  ereignisse: 60 * 1000,
  /** Glocke. Eine Benachrichtigung kann jederzeit entstehen. */
  glocke: 60 * 1000,
  /** Marktstatus (offen/geschlossen). Hängt an Handelszeiten, also an der Uhr. */
  marktstatus: 60 * 1000,
  /**
   * ⭐ EINE LAUFENDE RECHNUNG — nicht Marktdaten, sondern ein Job, auf den gewartet wird
   * (26.09.2026). Die Verlaufs-Rekonstruktion nach einem Import dauert gemessen bis zu rund
   * 98 Sekunden; bis dahin zeigt die Seite „Verlauf wird berechnet".
   * ⛔ DESHALB SO KURZ: Hier wartet jemand ZU. Fünf Sekunden sind der Unterschied zwischen
   * „die Seite merkt es" und „ich lade neu".
   * ⚠️ UND DESHALB NUR SOLANGE ER LÄUFT: Der Aufrufer schaltet ihn mit `aus` ab, sobald der
   * Lauf fertig ist — sonst fragte jede offene Seite dauerhaft alle fünf Sekunden.
   */
  laufenderVerlauf: 5 * 1000,
  /**
   * ⭐ LIVE-SESSIONS (Live-Sessions A, 23.10.2026): Der Raum-Link erscheint ab 60 Minuten vor
   * Beginn — die UHR ändert die Antwort, nicht der Nutzer. Eine Minute reicht: Wer die Seite offen
   * hat, sieht den Knopf spätestens eine Minute nach seiner Zeit.
   */
  liveTermine: 60 * 1000,
} as const;

// ════════════════════════════════════════════════════════════════════════════
// ⛔ Die Sperre — Formulare und Modale setzen JEDE Aktualisierung aus
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⛔ DIE EINE BEDINGUNG, DIE ÜBERALL GILT — nicht je Seite entschieden.
 *
 * Wer eine Buchung eingibt und dabei eine Aktualisierung bekommt, verliert womöglich seine
 * Eingabe: Die Liste darunter lädt neu, die Komponente rendert, und im schlimmsten Fall schließt
 * sich das Modal, weil sein Gegenstand verschwunden ist.
 *
 * ⭐ Deshalb ein ZÄHLER, kein Schalter: Zwei offene Ebenen (Modal über Modal, Bestätigung über
 * Formular) kommen vor, und das Schließen der inneren dürfte die Sperre nicht aufheben.
 */
let offeneEingaben = 0;
const zuhoerer = new Set<() => void>();

function melden(): void {
  for (const z of zuhoerer) z();
}

/** ⚠️ Nur für Tests — im Betrieb meldet sich jede Eingabe selbst an und ab. */
export function _setzeEingabenZurueck(): void {
  offeneEingaben = 0;
  melden();
}

export function eingabeGeoeffnet(): void {
  offeneEingaben += 1;
  melden();
}

export function eingabeGeschlossen(): void {
  // ⚠️ Nie unter null: Ein doppeltes Abmelden (Unmount nach Schließen) dürfte die Sperre sonst
  //    dauerhaft ins Minus drücken und damit unwirksam machen.
  offeneEingaben = Math.max(0, offeneEingaben - 1);
  melden();
}

export function eingabeOffen(): boolean {
  return offeneEingaben > 0;
}

function abonniere(f: () => void): () => void {
  zuhoerer.add(f);
  return () => { zuhoerer.delete(f); };
}

/** Reaktiv: Wer das liest, rendert neu, sobald sich die Sperre ändert. */
export function useEingabeOffen(): boolean {
  return useSyncExternalStore(abonniere, eingabeOffen, () => false);
}

/**
 * Meldet eine offene Eingabe an, solange `aktiv` gilt.
 *
 * ⭐ Für INLINE-Formulare, die kein `<Modal>` sind (etwa der Konten-Editor). Modale melden sich
 * selbst an — dort ist nichts zu tun.
 */
export function useEingabeSperre(aktiv: boolean): void {
  useEffect(() => {
    if (!aktiv) return;
    eingabeGeoeffnet();
    return eingabeGeschlossen;
  }, [aktiv]);
}

// ════════════════════════════════════════════════════════════════════════════
// Der Haken für die Abfragen
// ════════════════════════════════════════════════════════════════════════════

export interface AktualisierungsOptionen {
  /**
   * ⭐ Nur wenn der Reiter SICHTBAR ist. Ein Fenster im Hintergrund fragt nicht — React Query
   * kann das von sich aus, es muss nur ausdrücklich abgeschaltet werden.
   *
   * ⛔ Immer `false`. Die Eigenschaft steht hier, damit sie an EINER Stelle steht und nicht in
   * jeder Abfrage einzeln vergessen werden kann.
   */
  refetchInterval: number | false;
  refetchIntervalInBackground: false;
}

/**
 * Die Abfrage-Einstellungen für eine Ansicht, die sich selbst aktualisiert.
 *
 * ```ts
 * useQuery({ queryKey: […], queryFn: …, ...useAktualisierung(AKTUALISIERUNG.kurse) });
 * ```
 *
 * ⚠️ `aus` schaltet den Takt für diese eine Abfrage ab (etwa wenn keine Depot-Kennung vorliegt).
 * Die Sperre durch offene Eingaben gilt davon unabhängig und immer.
 */
export function useAktualisierung(takt: number, aus = false): AktualisierungsOptionen {
  const gesperrt = useEingabeOffen();
  return {
    /*
      ⚠️ `useEingabeOffen()` und NICHT `eingabeOffen()`: Nur der abonnierte Wert löst ein
      Neu-Rendern aus, und erst dadurch setzt React Query den Timer neu. Ein direkt gelesener
      Wert wäre beim ersten Rendern richtig und danach nie wieder.

      ⭐ Das genügt auch: Das Öffnen eines Modals ist eine Zustandsänderung, das Neu-Rendern
      folgt im selben Durchgang, und der alte Timer wird dabei verworfen.
    */
    refetchInterval: gesperrt || aus ? false : takt,
    refetchIntervalInBackground: false,
  };
}
